/**
 * AgentCreatePage.tsx
 *
 * Dual-purpose agent form:
 *   • Create (no agentId): creates the agent identity + its first version.
 *       POST /v1/agents                  → { id }
 *       POST /v1/agents/{id}/versions    → { id: versionId }
 *   • Edit (agentId set): updates agent-level fields only (name, description,
 *     status, execution mode, model). Instructions/persona live on the
 *     immutable agent_versions records and are not edited here.
 *       PUT /v1/agents/{id}              → updated Agent
 */

import { useEffect, useRef, useState } from 'react'

import { api, ApiError, apiErrorMessage } from '../api/client'
import { GlassPanel }    from './common/GlassPanel'
import { buildCron, parseCron, WEEKDAYS, type Frequency } from '../lib/scheduleCron'

// ── Field primitives (local — there is no shared FormField component) ─────────

const FIELD_INPUT =
  'w-full rounded-[9px] border border-[rgba(148,163,184,0.15)] bg-[rgba(6,10,16,0.55)] text-[12px] text-[#c8d9ee] leading-[1.55] px-[12px] py-[10px] outline-none transition-colors duration-[120ms] placeholder:text-[#2c3f55] focus:border-[rgba(56,189,248,0.4)]'
const FIELD_TEXTAREA = `${FIELD_INPUT} resize-y font-mono`
const FIELD_LABEL = 'text-[11px] font-bold text-[#8fa3bd] tracking-[0.04em]'
const FIELD_HINT = 'text-[10.5px] text-[#4e6480] font-normal'

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label htmlFor={htmlFor} className={FIELD_LABEL}>
        {label}
        {hint && <span className={`ml-[6px] ${FIELD_HINT}`}>{hint}</span>}
      </label>
      {children}
    </div>
  )
}

// ── API shapes ────────────────────────────────────────────────────────────────

interface CreatedAgent   { id: string }
interface CreatedVersion { id: string }

interface AgentRecord {
  id: string
  name: string
  description?: string
  status: string
  executionMode: string
  llmProvider?: string
  llmModel?: string
}

interface ScheduleRecord {
  id: string
  scheduleExpr: string
  status: string
}

type SubmitState =
  | { kind: 'idle'    }
  | { kind: 'submitting' }
  | { kind: 'error';   message: string }
  | { kind: 'success'; agentId: string; versionId?: string }

const AGENT_STATUSES = ['draft', 'active', 'paused', 'archived'] as const

// ── Model options ─────────────────────────────────────────────────────────────

interface ModelOption {
  label:    string
  provider: string
  model:    string
}

const MODEL_OPTIONS: readonly ModelOption[] = [
  { label: 'Claude Sonnet 4',  provider: 'anthropic', model: 'claude-sonnet-4-20250514' },
  { label: 'Claude Haiku 4.5', provider: 'anthropic', model: 'claude-haiku-4-5-20251001' },
  { label: 'GPT-4o',           provider: 'openai',    model: 'gpt-4o' },
  { label: 'GPT-4o Mini',      provider: 'openai',    model: 'gpt-4o-mini' },
  { label: 'Gemini 2.0 Flash', provider: 'gemini',    model: 'gemini-2.0-flash' },
]

const modelKey = (o: { provider: string; model: string }) => `${o.provider}/${o.model}`

// ── Component ─────────────────────────────────────────────────────────────────

export interface AgentCreatePageProps {
  /** When set, the form edits this agent instead of creating a new one. */
  agentId?: string
  /** Called after a successful create/update (e.g. to return to a list). */
  onDone?: () => void
}

export function AgentCreatePage({ agentId, onDone }: AgentCreatePageProps = {}) {
  const editing = !!agentId

  // Identity (agents table)
  const [projectId,     setProjectId]     = useState('')
  const [slug,          setSlug]          = useState('')
  const [name,          setName]          = useState('')
  const [description,   setDescription]   = useState('')
  const [executionMode, setExecutionMode] = useState<'async' | 'interactive'>('async')
  const [status,        setStatus]        = useState<string>('draft')
  // Model selection — stored as the "provider/model" key.
  const [model,         setModel]         = useState<string>(modelKey(MODEL_OPTIONS[0]))

  // Version (agent_versions table) — create-only.
  const [instructions, setInstructions] = useState('')
  const [role,         setRole]         = useState('')
  const [goal,         setGoal]         = useState('')
  const [backstory,    setBackstory]    = useState('')

  const [state, setState]     = useState<SubmitState>({ kind: 'idle' })
  const [loading, setLoading] = useState(editing)
  const [loadError, setLoadError] = useState<string | null>(null)

  // "Agent saved" toast (green, auto-dismiss after 3s).
  const [toastVisible, setToastVisible] = useState(false)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function fireToast() {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToastVisible(true)
    toastTimer.current = setTimeout(() => setToastVisible(false), 3000)
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current) }, [])

  const toastEl = toastVisible ? (
    <div
      className="fixed bottom-7 right-7 z-[200] inline-flex items-center gap-2 px-[16px] py-[11px] rounded-[10px] bg-[rgba(34,197,94,0.16)] border border-[rgba(34,197,94,0.4)] text-[#4ade80] text-[12.5px] font-bold shadow-[0_8px_28px_rgba(0,0,0,0.45)]"
      role="status"
      aria-live="polite"
    >
      <span aria-hidden="true">✓</span> Agent saved
    </div>
  ) : null

  // Schedule (edit mode only).
  const [schedEnabled, setSchedEnabled] = useState(false)
  const [schedFreq, setSchedFreq]       = useState<Frequency>('daily')
  const [schedTime, setSchedTime]       = useState('09:00')
  const [schedWeekday, setSchedWeekday] = useState(1)
  const [schedCustom, setSchedCustom]   = useState('0 * * * *')
  const [showCron, setShowCron]         = useState(false)
  // Existing schedule id + whether it was active on load (to decide create vs update/disable).
  const [scheduleId, setScheduleId]     = useState<string | null>(null)
  const [schedWasActive, setSchedWasActive] = useState(false)

  // ── Load the existing agent when editing ─────────────────────────────────────
  useEffect(() => {
    if (!agentId) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    api
      .get<AgentRecord>(`/v1/agents/${encodeURIComponent(agentId)}`)
      .then((agent) => {
        if (cancelled) return
        setName(agent.name ?? '')
        setDescription(agent.description ?? '')
        setExecutionMode(agent.executionMode === 'interactive' ? 'interactive' : 'async')
        setStatus(agent.status || 'draft')
        if (agent.llmProvider && agent.llmModel) {
          setModel(`${agent.llmProvider}/${agent.llmModel}`)
        }
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoadError(apiErrorMessage(err, 'Failed to load agent'))
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [agentId])

  // Load the existing schedule (edit mode) and pre-populate the section.
  useEffect(() => {
    if (!agentId) return
    let cancelled = false
    api
      .get<{ items?: ScheduleRecord[] }>('/v1/schedules', { params: { agentId } })
      .then((res) => {
        if (cancelled) return
        const items = res.items ?? []
        // Prefer an active schedule; otherwise the most recent one.
        const sched = items.find((s) => s.status === 'active') ?? items[0]
        if (!sched) return
        setScheduleId(sched.id)
        setSchedWasActive(sched.status === 'active')
        if (sched.status === 'active') {
          const p = parseCron(sched.scheduleExpr)
          setSchedEnabled(true)
          setSchedFreq(p.freq)
          setSchedTime(p.time)
          setSchedWeekday(p.weekday)
          if (p.freq === 'custom') { setSchedCustom(sched.scheduleExpr); setShowCron(true) }
        }
      })
      .catch(() => { /* no schedule / not reachable → leave defaults */ })
    return () => { cancelled = true }
  }, [agentId])

  // If the agent's current model isn't one of the presets, surface it too so the
  // <select> reflects reality rather than silently showing the first option.
  const modelOptions: ModelOption[] = (() => {
    if (MODEL_OPTIONS.some((o) => modelKey(o) === model)) return [...MODEL_OPTIONS]
    const [provider, mdl] = model.split('/', 2)
    if (!provider || !mdl) return [...MODEL_OPTIONS]
    return [{ label: `${mdl} (current)`, provider, model: mdl }, ...MODEL_OPTIONS]
  })()

  const canSubmit = editing
    ? name.trim() !== '' && state.kind !== 'submitting'
    : projectId.trim() !== '' && slug.trim() !== '' && name.trim() !== '' && instructions.trim() !== '' && state.kind !== 'submitting'

  // Create / update / disable the agent's schedule based on the section state.
  async function persistSchedule() {
    if (!agentId) return
    const expr = schedFreq === 'custom'
      ? schedCustom.trim()
      : buildCron({ freq: schedFreq, time: schedTime, weekday: schedWeekday })

    if (schedEnabled) {
      if (!expr) return
      if (scheduleId) {
        await api.patch(`/v1/schedules/${encodeURIComponent(scheduleId)}`, { scheduleExpr: expr, status: 'active' })
      } else {
        // UTC-explicit (slice C ruling): the scheduler evaluates cron in UTC
        // only, so collecting the browser timezone here was dishonest — the
        // stored value was never honored.
        const created = await api.post<{ id: string }>(`/v1/agents/${encodeURIComponent(agentId)}/schedules`, {
          name: `${name.trim() || 'Agent'} schedule`,
          scheduleKind: 'cron',
          scheduleExpr: expr,
          timezone: 'UTC',
        })
        setScheduleId(created.id)
        setSchedWasActive(true)
      }
    } else if (scheduleId && schedWasActive) {
      // Toggled off → disable (not delete).
      await api.patch(`/v1/schedules/${encodeURIComponent(scheduleId)}`, { status: 'disabled' })
      setSchedWasActive(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    setState({ kind: 'submitting' })
    const [llmProvider, llmModel] = model.split('/', 2)
    try {
      if (editing) {
        await api.put<AgentRecord>(`/v1/agents/${encodeURIComponent(agentId!)}`, {
          name:          name.trim(),
          description:   description.trim(),
          status,
          executionMode,
          llmProvider,
          llmModel,
        })
        await persistSchedule()
        fireToast()
        // Show the toast briefly before returning to the list (form unmounts).
        if (onDone) { setTimeout(onDone, 1500); return }
        setState({ kind: 'success', agentId: agentId! })
        return
      }

      // ── Create flow: agent identity + first version ──
      const agent = await api.post<CreatedAgent>('/v1/agents', {
        projectId:   projectId.trim(),
        slug:        slug.trim(),
        name:        name.trim(),
        description: description.trim() || undefined,
        executionMode,
        llmProvider,
        llmModel,
      })
      const version = await api.post<CreatedVersion>(
        `/v1/agents/${encodeURIComponent(agent.id)}/versions`,
        {
          instructions: instructions.trim(),
          role:         role.trim()      || undefined,
          goal:         goal.trim()      || undefined,
          backstory:    backstory.trim() || undefined,
        },
      )
      setState({ kind: 'success', agentId: agent.id, versionId: version.id })
      fireToast()
    } catch (err) {
      const fallback = editing ? 'Failed to update agent' : 'Failed to create agent'
      // Status number kept (operator surface), body routed through
      // apiErrorMessage; statusText dropped — often empty under HTTP/2.
      const message =
        err instanceof ApiError
          ? `${err.status} — ${apiErrorMessage(err, fallback)}`
          : apiErrorMessage(err, fallback)
      setState({ kind: 'error', message })
    }
  }

  function handleReset() {
    setProjectId('')
    setSlug('')
    setName('')
    setDescription('')
    setExecutionMode('async')
    setStatus('draft')
    setModel(modelKey(MODEL_OPTIONS[0]))
    setInstructions('')
    setRole('')
    setGoal('')
    setBackstory('')
    setState({ kind: 'idle' })
  }

  // ── Loading / load-error (edit mode) ─────────────────────────────────────────
  if (editing && loading) {
    return <div className="pt-2 max-w-[640px]"><div className="h-[280px] rounded-2xl border border-[rgba(148,163,184,0.1)] bg-[rgba(11,18,32,0.4)] animate-pulse" /></div>
  }
  if (editing && loadError) {
    return (
      <div className="pt-2 max-w-[640px]">
        <div className="text-[13px] text-[#f87171] px-4 py-3 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
          {loadError}{' '}
          {onDone && <button className="underline bg-transparent border-none text-[#f87171] cursor-pointer p-0" onClick={onDone}>Back</button>}
        </div>
      </div>
    )
  }

  // ── Success view ─────────────────────────────────────────────────────────────
  if (state.kind === 'success') {
    return (
      <div className="pt-2 max-w-[640px]">
        <GlassPanel glow="success" className="p-6 flex flex-col gap-3">
          <h2 className="text-[16px] font-[800] text-[#4ade80] m-0">{editing ? 'Agent updated' : 'Agent created'}</h2>
          <p className="text-[12px] text-[#8fa3bd] m-0 leading-[1.6]">
            Agent <code className="font-mono text-[#dce9f7]">{state.agentId}</code>
            {state.versionId ? <> and its first version <code className="font-mono text-[#dce9f7]">{state.versionId}</code> were created (draft — deploy to publish).</> : <> was updated.</>}
          </p>
          <button
            type="button"
            onClick={onDone ?? handleReset}
            className="self-start mt-1 px-[14px] py-[8px] rounded-[9px] border border-[rgba(56,189,248,0.4)] bg-[rgba(56,189,248,0.12)] text-[#38bdf8] text-[12px] font-bold cursor-pointer transition-colors hover:bg-[rgba(56,189,248,0.2)]"
          >
            {onDone ? 'Back to agents' : (editing ? 'Done' : 'Create another')}
          </button>
        </GlassPanel>
        {toastEl}
      </div>
    )
  }

  // ── Form view ─────────────────────────────────────────────────────────────────
  return (
    <div className="pt-2 max-w-[640px]">
      <div className="mb-7">
        <h1 className="text-[22px] font-[900] text-[#dce9f7] m-0 mb-2 tracking-[-0.03em]">
          {editing ? 'Edit Agent' : 'New Agent'}
        </h1>
        <p className="text-[13px] text-[#4e6480] leading-[1.65] m-0 max-w-[520px]">
          {editing
            ? 'Update the agent’s name, status, execution mode, and model. Instructions and persona are versioned separately and edited via new versions.'
            : 'Define a specialised persona. Role, goal, and backstory shape how the agent reasons; instructions define what it does.'}
        </p>
      </div>

      <GlassPanel className="p-6">
        <form className="flex flex-col gap-[18px]" onSubmit={handleSubmit}>
          {/* ── Identity ── */}
          {!editing && (
            <Field label="Project ID" htmlFor="agent-project" hint="required">
              <input
                id="agent-project"
                className={FIELD_INPUT}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="UUID of the owning project"
                spellCheck={false}
              />
            </Field>
          )}

          <div className={editing ? '' : 'grid grid-cols-2 gap-[14px]'}>
            <Field label="Name" htmlFor="agent-name" hint="required">
              <input
                id="agent-name"
                className={FIELD_INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Senior Recruiter"
              />
            </Field>
            {!editing && (
              <Field label="Slug" htmlFor="agent-slug" hint="required">
                <input
                  id="agent-slug"
                  className={FIELD_INPUT}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="senior-recruiter"
                  spellCheck={false}
                />
              </Field>
            )}
          </div>

          <Field label="Description" htmlFor="agent-description" hint="optional">
            <input
              id="agent-description"
              className={FIELD_INPUT}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short summary shown in the agent list"
            />
          </Field>

          {editing && (
            <Field label="Status" htmlFor="agent-status">
              <select
                id="agent-status"
                className={FIELD_INPUT}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {AGENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Execution mode" htmlFor="agent-mode">
            <select
              id="agent-mode"
              className={FIELD_INPUT}
              value={executionMode}
              onChange={(e) => setExecutionMode(e.target.value as 'async' | 'interactive')}
            >
              <option value="async">async</option>
              <option value="interactive">interactive</option>
            </select>
          </Field>

          <Field label="Model" htmlFor="agent-model" hint="LLM provider + model">
            <select
              id="agent-model"
              className={FIELD_INPUT}
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {modelOptions.map((o) => (
                <option key={modelKey(o)} value={modelKey(o)}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          {/* ── Schedule (edit-only) ── */}
          {editing && (
            <div className="flex flex-col gap-[12px] border-t border-[rgba(148,163,184,0.1)] pt-[16px]">
              <button
                type="button"
                role="switch"
                aria-checked={schedEnabled}
                onClick={() => setSchedEnabled((v) => !v)}
                className="inline-flex items-center gap-[8px] text-[12px] font-bold text-[#c8d9ee] cursor-pointer bg-transparent border-none p-0 self-start"
              >
                <span className={['relative w-[38px] h-[21px] rounded-full transition-colors duration-[140ms]', schedEnabled ? 'bg-[rgba(34,197,94,0.5)]' : 'bg-[rgba(148,163,184,0.2)]'].join(' ')}>
                  <span className={['absolute top-[2px] w-[17px] h-[17px] rounded-full bg-white transition-transform duration-[140ms]', schedEnabled ? 'translate-x-[19px]' : 'translate-x-[2px]'].join(' ')} />
                </span>
                Run on a schedule
              </button>

              {schedEnabled && (
                <div className="flex flex-col gap-[12px] pl-1">
                  {/* Frequency radios */}
                  <div className="flex flex-col gap-2">
                    {([
                      ['hourly', 'Every hour'],
                      ['daily',  'Every day'],
                      ['weekly', 'Every week'],
                    ] as const).map(([f, label]) => (
                      <label key={f} className="flex items-center gap-2 text-[12px] text-[#c8d9ee] cursor-pointer">
                        <input type="radio" name="sched-freq" checked={schedFreq === f} onChange={() => setSchedFreq(f)} />
                        {label}
                        {f === 'daily' && schedFreq === 'daily' && (
                          <span className="flex items-center gap-1.5 ml-2">
                            <input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className={`${FIELD_INPUT} max-w-[120px]`} />
                            <span className="text-[10px] text-[#4e6480]">UTC</span>
                          </span>
                        )}
                        {f === 'weekly' && schedFreq === 'weekly' && (
                          <span className="flex items-center gap-2 ml-2">
                            <select value={schedWeekday} onChange={(e) => setSchedWeekday(Number(e.target.value))} className={`${FIELD_INPUT} max-w-[110px]`}>
                              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                            </select>
                            <input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className={`${FIELD_INPUT} max-w-[120px]`} />
                            <span className="text-[10px] text-[#4e6480]">UTC</span>
                          </span>
                        )}
                      </label>
                    ))}
                  </div>

                  {/* Custom cron (advanced, collapsible) */}
                  <div>
                    <button
                      type="button"
                      onClick={() => { setShowCron((v) => !v); if (!showCron) setSchedFreq('custom') }}
                      className="text-[11px] text-[#4e6480] underline bg-transparent border-none cursor-pointer p-0 hover:text-[#8fa3bd]"
                    >
                      {showCron ? '− Hide' : '+ Advanced'} cron expression
                    </button>
                    {showCron && (
                      <div className="mt-2 flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-[11px] text-[#c8d9ee] cursor-pointer">
                          <input type="radio" name="sched-freq" checked={schedFreq === 'custom'} onChange={() => setSchedFreq('custom')} />
                          Custom
                        </label>
                        <input
                          className={`${FIELD_INPUT} font-mono max-w-[220px]`}
                          value={schedCustom}
                          onChange={(e) => { setSchedCustom(e.target.value); setSchedFreq('custom') }}
                          placeholder="0 9 * * 1"
                          spellCheck={false}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Instructions + persona (create-only; versioned records) ── */}
          {!editing && (
            <>
              <Field label="Instructions" htmlFor="agent-instructions" hint="required">
                <textarea
                  id="agent-instructions"
                  className={FIELD_TEXTAREA}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Step-by-step instructions the agent must follow…"
                  rows={5}
                  spellCheck={false}
                />
              </Field>

              <Field label="Role" htmlFor="agent-role" hint="optional">
                <input
                  id="agent-role"
                  className={FIELD_INPUT}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Senior Recruiter"
                />
              </Field>

              <Field label="Goal" htmlFor="agent-goal" hint="optional">
                <input
                  id="agent-goal"
                  className={FIELD_INPUT}
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="Screen CVs for Python engineering roles"
                />
              </Field>

              <Field label="Backstory" htmlFor="agent-backstory" hint="optional">
                <textarea
                  id="agent-backstory"
                  className={FIELD_TEXTAREA}
                  value={backstory}
                  onChange={(e) => setBackstory(e.target.value)}
                  placeholder="You have 10 years of experience in technical recruiting…"
                  rows={4}
                  spellCheck={false}
                />
              </Field>
            </>
          )}

          {/* ── Error ── */}
          {state.kind === 'error' && (
            <div
              role="alert"
              className="rounded-[9px] border border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.08)] px-[12px] py-[10px] text-[12px] text-[#fca5a5] leading-[1.5] break-words"
            >
              {state.message}
            </div>
          )}

          {/* ── Submit ── */}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-[16px] py-[10px] rounded-[9px] border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.16)] text-[#4ade80] text-[12px] font-[800] cursor-pointer transition-colors hover:enabled:bg-[rgba(34,197,94,0.26)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.kind === 'submitting'
                ? (editing ? 'Saving…' : 'Creating…')
                : (editing ? 'Save changes' : 'Create agent')}
            </button>
            {state.kind !== 'submitting' && (
              <button
                type="button"
                onClick={editing ? (onDone ?? handleReset) : handleReset}
                className="px-[14px] py-[10px] rounded-[9px] border border-[rgba(148,163,184,0.2)] bg-transparent text-[#8fa3bd] text-[12px] font-bold cursor-pointer transition-colors hover:bg-[rgba(148,163,184,0.08)]"
              >
                {editing ? 'Cancel' : 'Reset'}
              </button>
            )}
          </div>
        </form>
      </GlassPanel>
      {toastEl}
    </div>
  )
}
